import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Radio, Bell, LineChart, Shield, CheckCircle2 } from 'lucide-react';
import { iotSensors, iotBenefits } from '../data/mockData';

const IoTSensors = () => {
  return (
    <div className="page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            <div className="badge">Powered by Sensative</div>
            <h1 className="heading-1">Intelligent IoT Sensor Systems</h1>
            <p className="body-large">
              Full-Bin UK deploys Sensative's award-winning LoRaWAN sensors and Yggio IoT platform. Ultra-thin sensors with 10-year battery life for real-time monitoring of energy, air quality, occupancy, and leak detection.
            </p>
            <Link to="/contact" className="btn-primary">
              Discuss Your Needs
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Sensative Product Images - ONLY YOUR UPLOADED IMAGES */}
      <section className="section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Sensative Sensor Products</h2>
            <p className="section-subtitle">
              Ultra-thin sensors with 10-year battery life
            </p>
          </div>

          <div className="real-products-grid">
            <div className="real-product-card">
              <img 
                src="https://customer-assets.emergentagent.com/job_eco-solutions-uk/artifacts/rpan80ll_Ska%CC%88rmavbild%202025-11-15%20kl.%2016.30.58.png"
                alt="Sensative sensor product"
                loading="lazy"
              />
              <h3 className="heading-3">Sensative Sensors</h3>
              <p className="body-medium">Professional IoT sensors for smart building monitoring</p>
            </div>

            <div className="real-product-card">
              <img 
                src="https://customer-assets.emergentagent.com/job_eco-solutions-uk/artifacts/we2gzd8p_Ska%CC%88rmavbild%202025-11-15%20kl.%2016.38.35.png"
                alt="Sensative sensor system"
                loading="lazy"
              />
              <h3 className="heading-3">Complete System</h3>
              <p className="body-medium">Integrated sensor solution with gateway and monitoring</p>
            </div>

            <div className="real-product-card">
              <img 
                src="https://customer-assets.emergentagent.com/job_eco-solutions-uk/artifacts/e4r06ldr_Ska%CC%88rmavbild%202025-11-09%20kl.%2023.04.50.png"
                alt="Quick installation guide for Sensative sensors"
                loading="lazy"
              />
              <h3 className="heading-3">Easy Installation</h3>
              <p className="body-medium">Stick sensors in minutes—no wiring, no complexity required</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sensative Partnership */}
      <section className="section bg-section">
        <div className="container">
          <div className="sensative-info">
            <div className="sensative-content">
              <h2 className="heading-2">Powered by Sensative Technology</h2>
              <p className="body-large">
                Full-Bin UK is proud to deploy Sensative's industry-leading IoT sensors and Yggio platform. With nearly 500,000 sensors trusted worldwide, Sensative delivers Nordic standards of reliability, 10-year battery life, and seamless interoperability.
              </p>
              <div className="sensative-features">
                <div className="feature-badge">
                  <CheckCircle2 size={20} />
                  <span>EU Data Act Compliant</span>
                </div>
                <div className="feature-badge">
                  <CheckCircle2 size={20} />
                  <span>10-Year Battery Life</span>
                </div>
                <div className="feature-badge">
                  <CheckCircle2 size={20} />
                  <span>LoRaWAN & Z-Wave</span>
                </div>
                <div className="feature-badge">
                  <CheckCircle2 size={20} />
                  <span>Frost & Sullivan Award Winner</span>
                </div>
              </div>
              <a href="https://sensative.com" target="_blank" rel="noopener noreferrer" className="btn-secondary">
                Visit Sensative.com
                <ArrowRight size={20} />
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* Sensor Types */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Sensative Sensor Solutions</h2>
            <p className="section-subtitle">
              Ultra-thin, architect-approved sensors with downloadable apps for customizable behavior.
            </p>
          </div>

          <div className="sensor-grid">
            {iotSensors.map((sensor, index) => (
              <div key={index} className="sensor-card">
                <div className="sensor-icon">
                  <Radio size={32} />
                </div>
                <div className="sensor-tech-badge">{sensor.technology}</div>
                <h3 className="heading-3">{sensor.name}</h3>
                <p className="body-medium">{sensor.description}</p>
                <div className="sensor-applications">
                  <div className="applications-label">Ideal for:</div>
                  <ul className="applications-list">
                    {sensor.applications.map((app, i) => (
                      <li key={i}>{app}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Why Sensative Sensors</h2>
            <p className="section-subtitle">
              The architect's choice for smart buildings and cities.
            </p>
          </div>

          <div className="ai-grid">
            {iotBenefits.map((benefit, index) => (
              <div key={index} className="product-card">
                <div className="card-icon">
                  {index === 0 && <LineChart size={32} />}
                  {index === 1 && <Bell size={32} />}
                  {index === 2 && <Radio size={32} />}
                  {index === 3 && <Shield size={32} />}
                </div>
                <h3 className="product-card-title">{benefit.title}</h3>
                <p className="product-card-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Real-World Applications</h2>
          </div>

          <div className="image-content-split">
            <div className="split-image">
              <img 
                src="https://images.unsplash.com/photo-1644329770639-1a20809b82a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxzbWFydCUyMGJ1aWxkaW5nfGVufDB8fHx8MTc2MzIxODQ5Nnww&ixlib=rb-4.1.0&q=85"
                alt="Smart building systems"
                loading="lazy"
              />
            </div>
            <div className="use-cases-list">
              <div className="use-case-item">
                <h3 className="heading-3">Building Management</h3>
                <p className="body-medium">
                  Property managers monitor multiple sites from a single dashboard. Track energy usage, occupancy patterns, and equipment performance across portfolios.
                </p>
              </div>

              <div className="use-case-item">
                <h3 className="heading-3">Manufacturing Operations</h3>
                <p className="body-medium">
                  Industrial facilities use sensors for predictive maintenance, environmental compliance, and energy optimization. Reduce downtime and improve efficiency.
                </p>
              </div>

              <div className="use-case-item">
                <h3 className="heading-3">Logistics & Warehousing</h3>
                <p className="body-medium">
                  Monitor temperature-sensitive goods, optimize climate control costs, and ensure regulatory compliance with automated reporting.
                </p>
              </div>

              <div className="use-case-item">
                <h3 className="heading-3">Flexible Workspaces</h3>
                <p className="body-medium">
                  Smart occupancy detection enables dynamic HVAC and lighting control. Reduce costs in underutilized areas while ensuring comfort in active zones.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specs */}
      <section className="section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Technical Specifications</h2>
          </div>

          <div className="specs-grid">
            <div className="spec-item">
              <h3 className="heading-3">Connectivity</h3>
              <p className="body-medium">LoRaWAN and Z-Wave protocols. Long-range, low-power communication with encrypted data transmission. Compatible with standard gateways.</p>
            </div>
            <div className="spec-item">
              <h3 className="heading-3">Battery Life</h3>
              <p className="body-medium">Up to 10 years on Sensative Strips sensors. Ultra-low power consumption with minimal maintenance requirements.</p>
            </div>
            <div className="spec-item">
              <h3 className="heading-3">Yggio Platform</h3>
              <p className="body-medium">Technology-agnostic IoT platform. Full data sovereignty, EU compliance, standardized API. Cloud, Edge, or Hybrid deployment options.</p>
            </div>
            <div className="spec-item">
              <h3 className="heading-3">Design</h3>
              <p className="body-medium">Ultra-thin, discreet design. Architect-approved for aesthetic integration. Durable, Nordic-engineered for reliability in all conditions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-2">Start monitoring what matters</h2>
            <p className="body-large">
              Let's design a sensor solution tailored to your operational needs.
            </p>
            <Link to="/contact" className="btn-primary">
              Contact Us
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IoTSensors;
